import Phaser from 'phaser';

export class LoadingScene extends Phaser.Scene {
  private loadingBar?: Phaser.GameObjects.Graphics;
  private loadingText?: Phaser.GameObjects.Text;
  private progressBox?: Phaser.GameObjects.Graphics;
  private percentText?: Phaser.GameObjects.Text;
  private assetText?: Phaser.GameObjects.Text;
  private particles?: Phaser.GameObjects.Particles.ParticleEmitter;

  constructor() {
    super({ key: 'LoadingScene' });
  }

  preload(): void {
    // Set background color matching the game theme
    this.cameras.main.setBackgroundColor('#083e96');
    
    // Create animated background
    this.createAnimatedBackground();
    
    // Create loading screen UI
    this.createLoadingUI();

    // Load all game assets
    this.loadAssets();

    // Listen to loading events
    this.setupLoadingEvents();
  }

  private createAnimatedBackground(): void {
    // Create subtle moving stars/particles for background
    const graphics = this.add.graphics();
    
    // Draw some stars
    for (let i = 0; i < 50; i++) {
      const x = Phaser.Math.Between(0, 1080);
      const y = Phaser.Math.Between(0, 1920);
      const size = Phaser.Math.Between(1, 3);
      
      graphics.fillStyle(0xFFFFFF, Phaser.Math.FloatBetween(0.3, 0.8));
      graphics.fillCircle(x, y, size);
    }

    // Add subtle movement to the background
    this.tweens.add({
      targets: graphics,
      y: -100,
      duration: 20000,
      repeat: -1,
      ease: 'Linear'
    });
  }

  private createLoadingUI(): void {
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;

    // Game title
    this.add.text(centerX, centerY - 300, 'Kiloba 9 tầng mây', {
      fontSize: '72px',
      color: '#FFFFFF',
      fontFamily: 'gilroy-medium',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    // Loading title with animation
    this.loadingText = this.add.text(centerX, centerY - 150, 'Đang tải tài nguyên...', {
      fontSize: '36px',
      color: '#FFFFFF',
      fontFamily: 'gilroy-medium'
    }).setOrigin(0.5);

    // Add pulsing animation to loading text
    this.tweens.add({
      targets: this.loadingText,
      alpha: 0.5,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // Progress box background with shadow
    this.progressBox = this.add.graphics();
    this.progressBox.fillStyle(0x000000, 0.5);
    this.progressBox.fillRoundedRect(centerX - 205, centerY - 35, 410, 70, 15);
    this.progressBox.fillStyle(0x333333);
    this.progressBox.fillRoundedRect(centerX - 200, centerY - 30, 400, 60, 10);
    
    // Progress box border with glow effect
    this.progressBox.lineStyle(3, 0x00BFFF, 0.8);
    this.progressBox.strokeRoundedRect(centerX - 200, centerY - 30, 400, 60, 10);

    // Progress bar
    this.loadingBar = this.add.graphics();

    // Progress percentage text
    this.percentText = this.add.text(centerX, centerY, '0%', {
      fontSize: '28px',
      color: '#FFFFFF',
      fontFamily: 'gilroy-medium',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    // Asset name text
    this.assetText = this.add.text(centerX, centerY + 120, '', {
      fontSize: '20px',
      color: '#CCCCCC',
      fontFamily: 'gilroy-medium'
    }).setOrigin(0.5);

    // Loading dots animation
    const dots = this.add.text(centerX + 180, centerY - 150, '...', {
      fontSize: '36px',
      color: '#FFFFFF',
      fontFamily: 'gilroy-medium'
    }).setOrigin(0, 0.5);

    // Animate the dots
    this.tweens.add({
      targets: dots,
      alpha: 0,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Power2'
    });
  }

  private loadAssets(): void {
    // Load custom font first
    this.load.font('gilroy-medium', '/fonts/SVN-Gilroy-Medium.otf');
    
    // Load game assets
    this.load.image('sky', '/images/9cloud/sky.png');
    this.load.image('tower', '/images/9cloud/tower.png');
    this.load.image('character', '/images/9cloud/character.png');
    this.load.image('playButton', '/images/9cloud/play_button.png');
    this.load.image('cloud', '/images/9cloud/cloud.png');
    this.load.image('gameover', '/images/9cloud/gameover.png');
    this.load.image('bg_button', '/images/9cloud/bg_button.png');

    // Add some delay to show loading screen properly
    this.load.on('fileprogress', (file: any) => {
      console.log('Loading file:', file.key);
    });
  }

  private setupLoadingEvents(): void {
    // Update progress bar with gradient effect
    this.load.on('progress', (value: number) => {
      const centerX = this.cameras.main.centerX;
      const centerY = this.cameras.main.centerY;
      
      // Clear and redraw progress bar with gradient
      this.loadingBar?.clear();
      
      // Create gradient effect for progress bar
      const progressWidth = 380 * value;
      this.loadingBar?.fillGradientStyle(0x00FF7F, 0x00BFFF, 0x00FF7F, 0x00BFFF);
      this.loadingBar?.fillRoundedRect(centerX - 190, centerY - 20, progressWidth, 40, 8);
      
      // Add a subtle glow effect
      if (value > 0) {
        this.loadingBar?.lineStyle(2, 0xFFFFFF, 0.3);
        this.loadingBar?.strokeRoundedRect(centerX - 190, centerY - 20, progressWidth, 40, 8);
      }
      
      // Update percentage text with smooth animation
      const percent = Math.round(value * 100);
      this.percentText?.setText(`${percent}%`);
      
      // Add pulse effect when reaching certain milestones
      if (percent === 25 || percent === 50 || percent === 75 || percent === 100) {
        this.tweens.add({
          targets: this.percentText,
          scaleX: 1.2,
          scaleY: 1.2,
          duration: 200,
          yoyo: true,
          ease: 'Power2'
        });
      }
    });

    // Update file loading text with fade effect
    this.load.on('fileprogress', (file: any) => {
      // Fade out current text, then fade in new text
      this.tweens.add({
        targets: this.assetText,
        alpha: 0,
        duration: 100,
        onComplete: () => {
          this.assetText?.setText(`Đang tải: ${this.getAssetDisplayName(file.key)}`);
          this.tweens.add({
            targets: this.assetText,
            alpha: 1,
            duration: 200
          });
        }
      });
    });

    // When loading is complete
    this.load.on('complete', () => {
      this.percentText?.setText('100%');
      this.assetText?.setText('Hoàn thành! Đang khởi động game...');
      
      // Add completion effect
      this.tweens.add({
        targets: [this.loadingText, this.percentText],
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 300,
        yoyo: true,
        ease: 'Back.easeOut'
      });
      
      // Add a longer delay to show completion
      this.time.delayedCall(1000, () => {
        // Fade out loading screen
        this.cameras.main.fadeOut(500, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start('CloudTowerScene');
        });
      });
    });

    // Handle loading errors with better feedback
    this.load.on('loaderror', (file: any) => {
      console.error('Error loading file:', file.key);
      this.assetText?.setText(`Lỗi tải: ${file.key} - Đang thử lại...`).setColor('#FF6B6B');
      
      // Add error effect
      this.tweens.add({
        targets: this.assetText,
        x: this.cameras.main.centerX + 10,
        duration: 100,
        yoyo: true,
        repeat: 3,
        ease: 'Power2'
      });
    });
  }

  // Helper method to get display names for assets
  private getAssetDisplayName(key: string): string {
    const displayNames: { [key: string]: string } = {
      'sky': 'Hình nền bầu trời',
      'tower': 'Tòa tháp',
      'character': 'Nhân vật',
      'playButton': 'Nút chơi',
      'cloud': 'Đám mây',
      'gameover': 'Màn hình kết thúc',
      'gilroy-medium': 'Font chữ'
    };
    
    return displayNames[key] || key;
  }

  create(): void {
    // Scene is ready, loading will start automatically
    console.log('Loading scene created');
  }
}
